/*
 * Featured-collection tabs.
 *
 * On the storefront only the first tab's products are rendered server-side. The other tabs are
 * fetched on demand from the same section through the Section Rendering API
 * (?section_id=<section>&hb_tab=collection-<block id>), which returns exactly the markup the
 * inline <template> used to hold. Tabs are prefetched on hover/focus/touch and, once the section
 * scrolls near the viewport, in idle time, so a click is normally instant. Inside the theme
 * editor the section still renders <template> blocks and those are used directly.
 */
const hbFeaturedTabContent = new Map();

function updateFeaturedCollectionViewAll(targetParent, tab) {
  const viewAllLink = targetParent?.querySelector("[data-featured-collection-view-all]");
  if (!viewAllLink || !tab) return;

  const collectionUrl = tab.dataset.collectionUrl || "";
  const viewAllLabel = tab.dataset.viewAllLabel || "";

  if (!collectionUrl) {
    viewAllLink.hidden = true;
    viewAllLink.removeAttribute("href");
    viewAllLink.removeAttribute("aria-label");
    return;
  }

  viewAllLink.hidden = false;
  viewAllLink.href = collectionUrl;

  if (viewAllLabel) {
    viewAllLink.setAttribute("aria-label", viewAllLabel);
  } else {
    viewAllLink.removeAttribute("aria-label");
  }
}

function stripFeaturedTabAnimations(content) {
  if (!content) return;
  content.querySelectorAll(".scroll-trigger.animate--slide-in").forEach((element) => {
    element.classList.remove("scroll-trigger", "animate--slide-in");
    element.style.transform = "none";
    element.style.opacity = "1";
  });
}

function refreshFeaturedTabSwatches() {
  if (typeof applyProductSwatches === "function") {
    window.requestAnimationFrame(() => {
      applyProductSwatches();
    });
  }
}

function buildFeaturedTabUrl(tab) {
  const container = tab.closest(".featured-collection-container");
  const sectionId = container?.dataset.id;
  const target = tab.dataset.tab;
  if (!sectionId || !target) return null;

  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set("section_id", sectionId);
  url.searchParams.set("hb_tab", target);
  return url.toString();
}

function fetchFeaturedTabContent(tab, priority = "auto") {
  const target = tab.dataset.tab;
  if (!target) return Promise.resolve(null);
  if (hbFeaturedTabContent.has(target)) return hbFeaturedTabContent.get(target);

  const url = buildFeaturedTabUrl(tab);
  if (!url) return Promise.resolve(null);

  const request = fetch(url, { credentials: "same-origin", priority })
    .then((response) => (response.ok ? response.text() : ""))
    .then((html) => {
      if (!html) return null;

      const doc = new DOMParser().parseFromString(html, "text/html");
      let content = doc.getElementById(target);
      if (!content) {
        const template = doc.getElementById(`template-${target}`);
        content = template?.content?.querySelector?.(".tab-content") || null;
      }

      return content ? document.importNode(content, true) : null;
    })
    .catch(() => null)
    .then((content) => {
      if (!content) hbFeaturedTabContent.delete(target);
      return content;
    });

  hbFeaturedTabContent.set(target, request);
  return request;
}

function prefetchFeaturedTab(tab) {
  const target = tab?.dataset?.tab;
  if (!target) return;
  if (document.getElementById(target) || document.getElementById(`template-${target}`)) return;
  fetchFeaturedTabContent(tab, "low");
}

function insertFeaturedTabContent(targetParent, content) {
  const existing = Array.from(targetParent.querySelectorAll(".tab-content"));
  const anchor = existing[existing.length - 1];

  if (anchor) {
    anchor.after(content);
  } else {
    targetParent.appendChild(content);
  }

  stripFeaturedTabAnimations(content);
}

function showFeaturedTabContent(targetParent, content) {
  targetParent.querySelectorAll(".tab-content").forEach((c) => {
    const isActive = c === content;
    c.classList.toggle("active", isActive);
    // Ported from Staging-all's tabs a11y pass: keep inactive panels out of the
    // accessibility tree even if the CSS display rule fails to apply.
    c.hidden = !isActive;
  });
  // Tabs only point at a panel once that panel exists in the DOM (aria-controls must target a
  // real role="tabpanel" element).
  if (content?.id) {
    const owningTab = Array.from(targetParent.querySelectorAll('.tab[role="tab"]')).find(
      (t) => t.dataset.tab === content.id,
    );
    if (owningTab && content.getAttribute("role") === "tabpanel") {
      owningTab.setAttribute("aria-controls", content.id);
    }
  }
  refreshFeaturedTabSwatches();
}

function applyFeaturedTabHeaderState(targetParent, tab) {
  targetParent.querySelectorAll('.tab[role="tab"]').forEach((t) => {
    const isActive = t === tab;
    t.classList.toggle("active", isActive);
    t.setAttribute("aria-selected", isActive ? "true" : "false");
    t.tabIndex = isActive ? 0 : -1;
  });

  // Fallback for any non-role tabs still present
  targetParent.querySelectorAll(".tab:not([role='tab'])").forEach((t) => {
    t.classList.toggle("active", t === tab);
  });

  updateFeaturedCollectionViewAll(targetParent, tab);
}

function setActiveFeaturedCollectionTab(tab, { focusTab = false } = {}) {
  const target = tab.dataset.tab;
  const existingContent = document.getElementById(target);
  const template = document.getElementById(`template-${target}`);
  const targetParent = tab.closest(".featured-collection-container");
  if (!targetParent) return;

  applyFeaturedTabHeaderState(targetParent, tab);

  const requestId = `${Date.now()}-${Math.random()}`;
  targetParent.dataset.hbTabRequest = requestId;

  if (existingContent) {
    showFeaturedTabContent(targetParent, existingContent);
  } else if (template) {
    const clone = template.content.cloneNode(true);
    template.parentNode.insertBefore(clone, template);
    const newContent = document.getElementById(target);
    stripFeaturedTabAnimations(newContent);
    showFeaturedTabContent(targetParent, newContent);
  } else {
    // The previous tab stays visible until the requested one has arrived.
    tab.setAttribute("aria-busy", "true");
    fetchFeaturedTabContent(tab, "high").then((content) => {
      tab.removeAttribute("aria-busy");
      if (!content) {
        // The section request failed (network error, HTTP 429/5xx): put the header, aria state and
        // "View all" link back on the tab whose products are still showing, so the highlighted
        // tab never disagrees with the visible grid. The next click retries the fetch.
        // That tab is read from the grid, not remembered at click time: after two quick clicks
        // on unloaded tabs the header had already moved to the first of them, whose products
        // never showed.
        if (targetParent.dataset.hbTabRequest === requestId) {
          const shownContent = targetParent.querySelector(".tab-content.active");
          const shownTab =
            shownContent &&
            Array.from(targetParent.querySelectorAll(".tab")).find((t) => t.dataset.tab === shownContent.dataset.tab);
          if (shownTab && shownTab !== tab) applyFeaturedTabHeaderState(targetParent, shownTab);
        }
        return;
      }

      let inserted = document.getElementById(target);
      if (!inserted) {
        insertFeaturedTabContent(targetParent, content);
        inserted = content;
      }

      if (targetParent.dataset.hbTabRequest !== requestId) return;
      showFeaturedTabContent(targetParent, inserted);
    });
  }

  if (focusTab) {
    tab.focus();
  }
}

function scheduleFeaturedTabIdlePrefetch(container) {
  const tabs = Array.from(container.querySelectorAll('.tab[role="tab"]')).filter(
    (tab) => !tab.classList.contains("active"),
  );

  const run = (index) => {
    if (index >= tabs.length) return;

    const go = () => {
      const tab = tabs[index];
      const target = tab.dataset.tab;
      const alreadyAvailable =
        !target || document.getElementById(target) || document.getElementById(`template-${target}`);
      const pending = alreadyAvailable ? Promise.resolve(null) : fetchFeaturedTabContent(tab, "low");
      pending.finally(() => run(index + 1));
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(go, { timeout: 4000 });
    } else {
      window.setTimeout(go, 600);
    }
  };

  run(0);
}

// Handle first tab on page load to prevent initial animation issues
document.addEventListener("DOMContentLoaded", () => {
  const firstTabContent = document.querySelector(".tab-content.active");
  if (firstTabContent) {
    stripFeaturedTabAnimations(firstTabContent);
  }

  document.querySelectorAll(".featured-collection-container").forEach((collectionContainer) => {
    const activeTab = collectionContainer.querySelector(".tab.active");
    if (activeTab) {
      updateFeaturedCollectionViewAll(collectionContainer, activeTab);
    }
  });

  // Warm every non-active tab shortly after the page has loaded (tabs are 10 to 20 KB compressed),
  // so a click without a preceding hover is still instant. Sections already near the viewport are
  // warmed first.
  const containers = Array.from(document.querySelectorAll(".featured-collection-container")).filter(
    (container) => container.querySelectorAll('.tab[role="tab"]').length > 1,
  );
  if (!containers.length) return;

  const connection = navigator.connection;
  const allowWarmup = !connection || (!connection.saveData && !/(^|-)2g$/.test(connection.effectiveType || ""));

  const warmed = new WeakSet();
  const warm = (container) => {
    if (!allowWarmup || warmed.has(container)) return;
    warmed.add(container);
    scheduleFeaturedTabIdlePrefetch(container);
  };

  // The first featured collection sits right under the hero on phones, so the observer fires at
  // once; warming then would put a full section render and its parse inside the page load and LCP
  // window. Viewport warm-ups wait for window load; hover, focus and touch prefetch stay immediate.
  const afterLoad = (fn) =>
    document.readyState === "complete" ? fn() : window.addEventListener("load", fn, { once: true });

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          afterLoad(() => warm(entry.target));
        });
      },
      { rootMargin: "300px 0px" },
    );
    containers.forEach((container) => observer.observe(container));
  }

  if (allowWarmup) {
    const warmAll = () => {
      const run = () => containers.forEach(warm);
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(run, { timeout: 3000 });
      } else {
        window.setTimeout(run, 800);
      }
    };
    if (document.readyState === "complete") {
      window.setTimeout(warmAll, 500);
    } else {
      window.addEventListener("load", () => window.setTimeout(warmAll, 500), { once: true });
    }
  }
});

document.querySelectorAll(".tab").forEach((tab) => {
  tab.addEventListener("click", () => {
    setActiveFeaturedCollectionTab(tab);
  });

  ["pointerenter", "focus", "touchstart"].forEach((eventName) => {
    tab.addEventListener(eventName, () => prefetchFeaturedTab(tab), { passive: true });
  });

  tab.addEventListener("keydown", (event) => {
    if (!tab.getAttribute("role") || tab.getAttribute("role") !== "tab") return;

    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;
    const isVertical = tablist.getAttribute("aria-orientation") === "vertical";
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && !isVertical) return;
    if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && isVertical) return;

    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;

    let nextIndex = null;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveFeaturedCollectionTab(tabs[nextIndex], { focusTab: true });
  });
});
